/**
 * @fileoverview Compact inline What's New card for the hamburger menu.
 * Shows the latest update with auto-playing video or image thumbnail.
 */

import { useState, useCallback } from 'react';
import { Badge } from '@/components/ui/badge';
import { Sparkles, X } from 'lucide-react';
import type { AppUpdate } from '@/hooks/useAppUpdates';

const DISMISSED_KEY = 'whats-new-card-dismissed';

function getDismissedId(): string | null {
  try { return localStorage.getItem(DISMISSED_KEY); } catch { return null; }
}

function setDismissedId(id: string) {
  try { localStorage.setItem(DISMISSED_KEY, id); } catch {}
}

interface InlineWhatsNewCardProps {
  latestUpdate: AppUpdate;
  isUnread: boolean;
  totalUnread: number;
  onNavigate: (path: string) => void;
}

export function InlineWhatsNewCard({ latestUpdate, isUnread, totalUnread, onNavigate }: InlineWhatsNewCardProps) {
  const update = latestUpdate as AppUpdate & { video_url?: string | null };
  const hasVideo = !!update.video_url;
  const hasImage = !!update.image_url;

  const [dismissed, setDismissed] = useState(() => getDismissedId() === update.id);

  const handleDismiss = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setDismissedId(update.id);
    setDismissed(true);
  }, [update.id]);

  if (dismissed) return null;

  return (
    <div className="px-4 py-2">
      <div className="relative">
        <button
          onClick={() => onNavigate('/whats-new')}
          className="w-full rounded-xl border border-border bg-card overflow-hidden text-left hover:bg-accent/50 transition-colors duration-200"
        >
          {/* Video or Image */}
          {hasVideo ? (
            <video
              src={update.video_url!}
              autoPlay
              loop
              muted
              playsInline
              className="w-full aspect-video object-cover"
            />
          ) : hasImage ? (
            <img
              src={update.image_url!}
              alt=""
              className="w-full aspect-video object-cover"
            />
          ) : null}

          {/* Content */}
          <div className="p-3">
            <div className="flex items-center gap-2 mb-0.5">
              {isUnread && (
                <span className="w-2 h-2 rounded-full bg-primary shrink-0" />
              )}
              <Sparkles className="w-3.5 h-3.5 text-primary shrink-0" />
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">What's New</span>
              {update.version && (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 shrink-0">
                  v{update.version}
                </Badge>
              )}
            </div>
            <p className="text-sm font-medium text-foreground line-clamp-1">{update.title}</p>
            <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{update.content}</p>
          </div>
        </button>

        {/* Dismiss button */}
        <button
          onClick={handleDismiss}
          className="absolute top-2 right-2 p-1 rounded-full bg-black/50 hover:bg-black/70 text-white/80 hover:text-white transition-colors z-10"
          aria-label="Dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {totalUnread > 1 && (
        <button
          onClick={() => onNavigate('/whats-new')}
          className="w-full text-center text-xs text-primary font-medium py-1.5 hover:underline"
        >
          See all {totalUnread} updates →
        </button>
      )}
    </div>
  );
}
